import { TaskPriority, TaskStatus } from 'api/types/tasks';
import { AlertTriangle, Paperclip, Star } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CollaborateTaskData } from 'types/api/collaborate';
import { Page } from 'types/api/site';
import { BorderBeam, Separator, ShineBorder } from 'ui';
import { AvatarStack } from 'ui/components/AvatarStack';
import Badge from 'ui/components/Badge';
import Button from 'ui/components/Button';
import { Tooltip } from 'ui/components/Tooltip';
import { cn } from 'utils/cn';
import { statusAvatarColor, urgencyAvatarColor } from 'utils/constants';
import { getHostAndPath, isInIframe, normalizeUrl } from 'utils/proxyUtils';
import { useResponsive } from 'utils/useMediaQuery';

import { enterpriseClients } from '../../../hooks/use-enterprise-account';
import { formatDateWithIntl } from '../../../hooks/use-format-date';
import { getSelectedSiteCollaborateInfo, getSelectedSiteId } from '../../../store/siteInfoSlice';
import {
  addLostTask,
  getLoadingAIComments,
  getLostTasks,
  getSelectedTaskId,
  setCreateModalOpen,
  setGeneralTaskModalOpen,
  setPageId,
  setSelectedTaskId
} from '../../../store/tasksSlice';
import { scrollToTaskElement } from '../../../utils/node';
import { StickerTypingIndicator } from '../../tasks/sticker-typing-indicator';
import { TypingIndicator } from '../../tasks/typing-indicator';
import { priorityIcons, statusIcons } from './icons';
import TaskSticky from './task-sticky';

interface Props {
  task: Partial<CollaborateTaskData>;
  index?: string | number;
  pages?: Page[];
  isActiveSticker?: boolean;
  isGenerating?: boolean;
}

export const Task = ({ task, index, pages, isActiveSticker, isGenerating }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loadingAiComments = useSelector(getLoadingAIComments);
  const selectedTaskId = useSelector(getSelectedTaskId);
  const siteId = useSelector(getSelectedSiteId);
  const { isDesktop } = useResponsive();
  const lostTasks = useSelector(getLostTasks);
  const itemDetails = useSelector(getSelectedSiteCollaborateInfo)
  const isLost = task.task_id && lostTasks.includes(task.task_id);
  const [taskHovered, setTaskHovered] = useState(false);

  const isGeneralTask = task.task_type &&
    (Array.isArray(task.task_type)
      ? task.task_type.some(type => type === 'General')
      : task.task_type === 'General');

  const handleTaskClick = async () => {
    if (selectedTaskId === task.task_id || task.task_id?.includes('OPTIMISTIC')) return;
    try {


      if (!isTaskOnCurrentPage(task)) {
        const urlToUse = isInIframe() ? task.task_page_url : task.page_url;
        const normalizedUrl = normalizeUrl(urlToUse!, siteId);
        if (!normalizedUrl) return;
        const pageUrl = new URL(normalizedUrl);
        const pagefound = pages?.find((p) => p.iframe_url === task.task_page_url || p.target_url === task.page_url);
        dispatch(setPageId(pagefound?.id || ''));
        pageUrl.searchParams.append('taskid', task.task_id!);
        pageUrl.searchParams.append('pageId', pagefound?.id || '');
        dispatch(setSelectedTaskId(task.task_id!));
        window.location.href = pageUrl.toString();
        return;
      }

      dispatch(setSelectedTaskId(task.task_id!));

      // Immediately determine which modal to open based on cached data
      if (isLost || isGeneralTask) {
        dispatch(setGeneralTaskModalOpen(task.task_id!));
      } else {
        // First try to scroll to the element, then open the modal
        if (task.task_element_path) {
          try {
            await scrollToTaskElement(task.task_element_path);
            // If scroll succeeds, open the create modal
            dispatch(setCreateModalOpen(task.task_id!));
          } catch (error) {
            console.warn('Failed to scroll to task element from sidebar:', error);
            // If scroll fails, treat as lost task and open general modal
            dispatch(addLostTask(task.task_id!));
            dispatch(setGeneralTaskModalOpen(task.task_id!));
          }
        } else {
          // No element path, open create modal directly
          dispatch(setCreateModalOpen(task.task_id!));
        }
      }
    } catch (error) {
      console.error('Error handling task click:', error);
    }
  };

  const hasPermission = useMemo(() => {
    return itemDetails?.result?.role === 'admin' || itemDetails?.result?.role === 'contributor';
  }, [itemDetails?.result?.role]);


  const isTaskOnCurrentPage = useCallback((task: Partial<CollaborateTaskData>) => {
    try {
      if (task?.task_id?.includes('OPTIMISTIC')) return true;
      if (!task?.page_url) return false;
      const pageResult = getHostAndPath(window.location.href);
      if (!pageResult) return false;
      const taskResult = getHostAndPath(task.page_url);
      if (!taskResult) return false;
      if (isInIframe()) {
        // check just the paths as the proxy will mess up the host
        return pageResult.path === taskResult.path;
      }
      return pageResult.host === taskResult.host && pageResult.path === taskResult.path;
    } catch (_e) {
      return false;
    }
  }, []);

  const isUnread = task.is_unread;
  const isPinned = task.is_pinned;

  const hasFileAttached = () =>
    task.task_comment_message === '<p><br></p>' ||
    task.task_comment_message === '' ||
    task.task_comment_message === null ||
    task.task_comment_message === undefined ||
    task.task_comment_message.includes('<img') ||
    task.task_comment_message.includes('<video') ||
    task.task_comment_message.includes('<audio') ||
    task.task_comment_message.includes('<iframe') ||
    task.task_comment_message.includes('<embed') ||
    task.task_comment_message.includes('<object') ||
    task.task_comment_message.includes('<applet') ||
    task.task_comment_message.includes('<param');

  const extractTextFromHTML = (html: string) => {
    if (hasFileAttached()) return t('task.file_attachment', 'File attachment');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const priorityColor = urgencyAvatarColor[task.task_priority as keyof typeof urgencyAvatarColor];
  const statusColor = statusAvatarColor[task.task_status as keyof typeof statusAvatarColor];
  const isGodaddyGuest = itemDetails?.result?.godaddy_username || (itemDetails?.result.account_id !== undefined && enterpriseClients.includes(itemDetails.result.account_id));

  return (
    <>
      {
        task.page_approved_at && index !== 0 && isGodaddyGuest &&
        <Tooltip content={t('task.tooltip.approved', 'Page approved: {{date}}', { date: task.page_approved_at })} side='top'>
          <div className='py-2 cursor-pointer'>
            <Separator className='bg-destructive' />
          </div>
        </Tooltip>
      }
      <Button
        onClick={handleTaskClick}
        variant="ghost"
        onMouseEnter={() => setTaskHovered(true)}
        onMouseLeave={() => setTaskHovered(false)}
        tooltipSide={isDesktop ? 'right' : 'bottom'}
        className={cn(
          'flex h-28 w-full gap-2 rounded-lg border-2 border-border p-2 bg-background relative',
          !isUnread && 'text-gray-500 bg-background/20 dark:text-textcolor',
          selectedTaskId == task.task_id && !isGenerating && 'border-tertiary',
          isGenerating && 'border border-transparent'
        )}
      >
        {isGenerating && (
          <div className='absolute inset-0 rounded-lg overflow-hidden'>
            <ShineBorder borderWidth={2} duration={12} shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />
            <BorderBeam
              duration={4}
              size={125}
              borderWidth={2}
              className="from-transparent via-tertiary to-transparent"
            />
            <BorderBeam
              duration={4}
              delay={2}
              size={125}
              borderWidth={2}
              className="from-transparent via-ata-orange to-transparent"
            />
          </div>
        )}
        <div className="flex h-full w-8 flex-col items-center justify-between">
          <div className='relative'>
            <TaskSticky task={task} taskHovered={taskHovered} />
            {
              isActiveSticker && <StickerTypingIndicator />}
          </div>
          {/* The unnread ew tag is moved to the top right corner of the task to avoid overlapping with the sticker typing indicator */}
          {isLost && (
            <Tooltip portal content={t('task.lost', 'Element not found on page')}>
              <AlertTriangle
                className="mb-1"
                size={20}
                color="#f59e0b" // Amber warning color
                strokeWidth={1.5}
                aria-label={t('task.aria.lost', 'Task element not found')}
              />
            </Tooltip>
          )}
          {hasPermission && (
            <Star
              className='mb-1'
              fill={isPinned ? 'gold' : 'transparent'}
              size={20}
              color={isPinned ? 'gold' : '#9ca3af'}
              strokeWidth={1.5}
              aria-label={isPinned ? t('task.pinned', 'Pinned task') : t('task.not_pinned', 'Not pinned')}
            />
          )}
        </div>
        <div className="flex h-full flex-1 flex-col gap-1 overflow-hidden text-left">
          <div className="leading-0 inline-flex items-center text-sm gap-2">
            <span className="truncate font-medium max-w-[130px]">{task.task_created_by}</span>
            {hasFileAttached() && (
              <Tooltip content={t('task.tooltip.has_attachments', 'This comment has items attached')}>
                <Paperclip size={14} strokeWidth={2} />
              </Tooltip>
            )}
            <Tooltip content={formatDateWithIntl(task.task_created_time ?? '')}>
              <span className="font-normal text-xs text-gray-400 tracking-normal">
                {t('task.created_ago', '{{time}} ago', { time: task.task_created_time_ago })}
              </span>
            </Tooltip>
            {isGenerating ? <Badge variant="generating" className="ml-auto">{t('task.ai.badge', 'THINKING')}</Badge> :
              isUnread ? <Badge variant="tertiary" className="ml-auto">{t('task.badge.new', 'New')}</Badge> : null}
          </div>
          {task.task_id && loadingAiComments[task.task_id] === 'running' ? (
            <TypingIndicator taskId={task.task_id}
              onlyAI={true}
              className="text-wrap text-sm font-normal max-w-[200px] truncate items-start mt-0 leading-0"
              indicatorClassName="hidden"
            />
          ) : (
            <p className="line-clamp-2 text-wrap text-sm font-normal">
              {extractTextFromHTML(task.task_title!)}
            </p>
          )}
          <div className="mt-auto flex justify-between">
            <div className="flex w-full items-center justify-between gap-1 overflow-hidden text-xs text-gray-400">
              {task.task_type && task.task_type.length > 0 && task.task_type.includes('General') && (
                <span className="max-w-12 truncate">
                  General
                </span>
              )}
              <span className="max-w-12 truncate">
                {task.task_page_title}
              </span>
              <div className="flex items-center gap-1">
                {hasPermission && (
                  <>
                    <Tooltip
                      content={t('task.tooltip.status', 'Status: {{status}}', { status: task.task_status })}
                      className="capitalize grow"
                    >
                      {statusIcons(task.task_status as TaskStatus, statusColor)}
                    </Tooltip>

                    <Tooltip
                      content={t('task.tooltip.priority', 'Priority: {{priority}}', { priority: task.task_priority })}
                      className="capitalize"
                    >
                      {priorityIcons(task.task_priority as TaskPriority, priorityColor)}
                    </Tooltip>
                  </>
                )}
                <AvatarStack
                  users={task.notify_wp_feedback_users || []}
                  maxVisible={3}
                />
              </div>
            </div>
          </div>
        </div>
      </Button>
    </>
  );
};